/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  AppState,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Linking,
  Share,
  Image,
  SectionList,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
  Entypo,
  FontAwesome,
  SimpleLineIcons,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';
import { Video } from 'expo-video';
import { Audio } from 'expo-audio'; // if you need audio
import { BlurView } from 'expo-blur';
import { BarChart, ProgressChart } from 'react-native-chart-kit';

const { width, height } = Dimensions.get('window');

const LearningHomeScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.95));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [greeting, setGreeting] = useState('Good morning');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Learning Data States
  const [todayTasks, setTodayTasks] = useState([]);
  const [weeklyProgress, setWeeklyProgress] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [learningGoals, setLearningGoals] = useState([]);
  const [quickAccess, setQuickAccess] = useState([]);
  const [stats, setStats] = useState({});
  
  // Modal States
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [newGoal, setNewGoal] = useState({ title: '', target: '', deadline: '' });
  
  // Charts Data
  const [progressData, setProgressData] = useState({
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      data: [0.7, 0.8, 0.6, 0.9, 0.7, 0.8, 0.9]
    }]
  });

  const [timeData, setTimeData] = useState({
    labels: ['Courses', 'Practice', 'Reading', 'Projects', 'Research'],
    datasets: [{
      data: [12, 8, 5, 6, 4]
    }]
  });

  // Animation refs
  const fadeIn = useRef(new Animated.Value(0)).current;
  const slideUp = useRef(new Animated.Value(20)).current;

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
    setupGreeting();
    startAnimations();
    
    // Set up notification listener
    const notificationListener = Notifications.addNotificationReceivedListener(notification => {
      console.log('Notification received:', notification);
    });

    return () => {
      notificationListener.remove();
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      refreshData();
    }, [])
  );

  const setupGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  };

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadUserData(),
        loadTodayTasks(),
        loadWeeklyProgress(),
        loadUpcomingSessions(),
        loadRecentActivity(),
        loadLearningGoals(),
        loadQuickAccess(),
        loadStats(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadUserData(),
        loadTodayTasks(),
        loadWeeklyProgress(),
        loadUpcomingSessions(),
        loadRecentActivity(),
        loadLearningGoals(),
        loadStats(),
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadUserData = async () => {
    try {
      const [storedUser, storedUserData] = await Promise.all([
        AsyncStorage.getItem('user'),
        AsyncStorage.getItem('userData'),
      ]);
      
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      } else if (storedUserData) {
        setUserData(JSON.parse(storedUserData));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      throw error;
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadTodayTasks = async () => {
    try {
      const tasks = [
        {
          id: '1',
          title: 'Complete Python Assignment',
          course: 'Python for Engineers',
          time: '10:00 AM',
          duration: '2 hours',
          priority: 'high',
          completed: false,
          type: 'assignment',
        },
        {
          id: '2',
          title: 'Watch Structural Analysis Lecture',
          course: 'Advanced Structural Analysis',
          time: '2:00 PM',
          duration: '1.5 hours',
          priority: 'medium',
          completed: true,
          type: 'lecture',
        },
        {
          id: '3',
          title: 'Solve Engineering Math Problems',
          course: 'Engineering Mathematics',
          time: '4:00 PM',
          duration: '1 hour',
          priority: 'medium',
          completed: false,
          type: 'practice',
        },
        {
          id: '4',
          title: 'Read ML Book Chapter 5',
          course: 'Machine Learning for Engineers',
          time: '6:00 PM',
          duration: '45 mins',
          priority: 'low',
          completed: false,
          type: 'reading',
        },
        {
          id: '5',
          title: 'Group Project Meeting',
          course: 'Engineering Design',
          time: '7:00 PM',
          duration: '1.5 hours',
          priority: 'high',
          completed: false,
          type: 'meeting',
        },
      ];
      setTodayTasks(tasks);
    } catch (error) {
      console.error('Error loading today tasks:', error);
      throw error;
    }
  };

  const loadWeeklyProgress = async () => {
    try {
      const progress = [
        { day: 'Mon', hours: 4.5, completed: true },
        { day: 'Tue', hours: 6, completed: true },
        { day: 'Wed', hours: 5, completed: true },
        { day: 'Thu', hours: 4, completed: false },
        { day: 'Fri', hours: 3.5, completed: false },
        { day: 'Sat', hours: 2, completed: false },
        { day: 'Sun', hours: 0, completed: false },
      ];
      setWeeklyProgress(progress);
    } catch (error) {
      console.error('Error loading weekly progress:', error);
      throw error;
    }
  };

  const loadUpcomingSessions = async () => {
    try {
      const sessions = [
        {
          id: '1',
          title: 'Live: CAD Design Workshop',
          instructor: 'Prof. Michael Chen',
          time: 'Tomorrow, 10:00 AM',
          duration: '2 hours',
          type: 'live',
          platform: 'Zoom',
          joinLink: 'https://zoom.us/join',
        },
        {
          id: '2',
          title: 'Webinar: AI in Engineering',
          instructor: 'Dr. Sarah Johnson',
          time: 'Mar 15, 3:00 PM',
          duration: '1.5 hours',
          type: 'webinar',
          platform: 'Microsoft Teams',
          joinLink: 'https://teams.microsoft.com',
        },
        {
          id: '3',
          title: 'Tutorial: Finite Element Analysis',
          instructor: 'Dr. Robert Williams',
          time: 'Mar 18, 11:00 AM',
          duration: '3 hours',
          type: 'tutorial',
          platform: 'Google Meet',
          joinLink: 'https://meet.google.com',
        },
      ];
      setUpcomingSessions(sessions);
    } catch (error) {
      console.error('Error loading upcoming sessions:', error);
      throw error;
    }
  };

  const loadRecentActivity = async () => {
    try {
      const activities = [
        {
          id: '1',
          title: 'Completed Python Module 3',
          course: 'Python for Engineers',
          time: '2 hours ago',
          type: 'completion',
          score: 95,
        },
        {
          id: '2',
          title: 'Submitted Structural Assignment',
          course: 'Advanced Structural Analysis',
          time: '5 hours ago',
          type: 'submission',
          score: null,
        },
        {
          id: '3',
          title: 'Earned Certificate: CAD Fundamentals',
          course: 'Mechanical Engineering Design',
          time: '1 day ago',
          type: 'certificate',
          score: 92,
        },
        {
          id: '4',
          title: 'Joined Study Group',
          course: 'Engineering Mathematics',
          time: '2 days ago',
          type: 'social',
          score: null,
        },
        {
          id: '5',
          title: 'Watched 5 Lecture Videos',
          course: 'Machine Learning for Engineers',
          time: '3 days ago',
          type: 'watching',
          score: null,
        },
      ];
      setRecentActivity(activities);
    } catch (error) {
      console.error('Error loading recent activity:', error);
      throw error;
    }
  };

  const loadLearningGoals = async () => {
    try {
      const goals = [
        {
          id: '1',
          title: 'Complete Python Course',
          target: 'Finish all modules',
          progress: 65,
          deadline: '2024-04-15',
          priority: 'high',
          type: 'course',
        },
        {
          id: '2',
          title: 'Master Structural Analysis',
          target: 'Score 90%+ in final exam',
          progress: 40,
          deadline: '2024-05-20',
          priority: 'high',
          type: 'exam',
        },
        {
          id: '3',
          title: 'Build Engineering Portfolio',
          target: 'Complete 5 projects',
          progress: 20,
          deadline: '2024-06-30',
          priority: 'medium',
          type: 'project',
        },
        {
          id: '4',
          title: 'Improve Math Skills',
          target: 'Solve 100+ problems',
          progress: 75,
          deadline: '2024-04-30',
          priority: 'medium',
          type: 'practice',
        },
      ];
      setLearningGoals(goals);
    } catch (error) {
      console.error('Error loading learning goals:', error);
      throw error;
    }
  };

  const loadQuickAccess = async () => {
    try {
      const accessItems = [
        {
          id: '1',
          title: 'Video Library',
          subtitle: '500+ engineering videos',
          icon: 'video-box',
          color: '#667eea',
          screen: 'VideoLibrary',
        },
        {
          id: '2',
          title: 'Practice Exams',
          subtitle: 'Test your knowledge',
          icon: 'clipboard-text',
          color: '#f59e0b',
          screen: 'PracticeExams',
        },
        {
          id: '3',
          title: 'E-Books',
          subtitle: '100+ engineering books',
          icon: 'book-open-variant',
          color: '#10b981',
          screen: 'EngineeringBooksScreen',
        },
        {
          id: '4',
          title: 'Study Groups',
          subtitle: 'Connect with peers',
          icon: 'account-group',
          color: '#8b5cf6',
          screen: 'StudyGroups',
        },
        {
          id: '5',
          title: 'Research Papers',
          subtitle: 'Latest engineering research',
          icon: 'file-document',
          color: '#ec4899',
          screen: 'ResearchPapers',
        },
        {
          id: '6',
          title: 'AI Assistant',
          subtitle: 'Get learning help',
          icon: 'robot',
          color: '#06b6d4',
          screen: 'AIAssistant',
        },
      ];
      setQuickAccess(accessItems);
    } catch (error) {
      console.error('Error loading quick access:', error);
      throw error;
    }
  };

  const loadStats = async () => {
    try {
      const statsData = {
        totalHours: 156,
        completedCourses: 8,
        currentCourses: 4,
        certificates: 5,
        streakDays: 14,
        averageScore: 87,
        efficiency: 76,
      };
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
      throw error;
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 700,
        useNativeDriver: true,
      }),
      Animated.timing(fadeIn, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideUp, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const toggleTaskCompletion = async (taskId) => {
    setTodayTasks(prev => 
      prev.map(task => 
        task.id === taskId ? { ...task, completed: !task.completed } : task
      )
    );
    
    // Save to AsyncStorage
    try {
      await AsyncStorage.setItem('todayTasks', JSON.stringify(todayTasks));
    } catch (error) {
      console.error('Error saving task completion:', error);
    }
  };

  const addNewTask = async () => {
    if (!newTask.trim()) return;

    const task = {
      id: Date.now().toString(),
      title: newTask,
      course: 'General',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      duration: '1 hour',
      priority: 'medium',
      completed: false,
      type: 'custom',
    };

    setTodayTasks(prev => [task, ...prev]);
    setNewTask('');
    setShowTaskModal(false);

    try {
      await AsyncStorage.setItem('todayTasks', JSON.stringify([task, ...todayTasks]));
    } catch (error) {
      console.error('Error saving new task:', error);
    }
  };

  const addNewGoal = async () => {
    if (!newGoal.title.trim() || !newGoal.target.trim()) return;

    const goal = {
      id: Date.now().toString(),
      title: newGoal.title,
      target: newGoal.target,
      progress: 0,
      deadline: newGoal.deadline || '2024-12-31',
      priority: 'medium',
      type: 'custom',
    };

    setLearningGoals(prev => [goal, ...prev]);
    setNewGoal({ title: '', target: '', deadline: '' });
    setShowGoalModal(false);

    try {
      await AsyncStorage.setItem('learningGoals', JSON.stringify([goal, ...learningGoals]));
    } catch (error) {
      console.error('Error saving new goal:', error);
    }
  };

  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    await AsyncStorage.setItem('darkMode', JSON.stringify(newDarkMode));
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    
    const fullName = userData.full_name || userData.fullName || userData.name;
    if (fullName) return fullName;
    
    const firstName = userData.first_name || userData.firstName || '';
    const lastName = userData.last_name || userData.lastName || '';
    const combinedName = `${firstName} ${lastName}`.trim();
    
    return combinedName || 'Student';
  };

  const getUserEducation = () => {
    if (!userData) return 'Engineering Student';
    return userData.education_level || userData.educationLevel || userData.education || 'Engineering Student';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'assignment': return 'clipboard-text';
      case 'lecture': return 'video';
      case 'practice': return 'calculator';
      case 'reading': return 'book-open';
      case 'meeting': return 'account-group';
      case 'completion': return 'check-circle';
      case 'submission': return 'upload';
      case 'certificate': return 'certificate';
      case 'social': return 'account-group';
      case 'watching': return 'eye';
      default: return 'circle';
    }
  };

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    pink: ['#d53f8c', '#fbb6ce'],
    teal: ['#319795', '#81e6d9'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
  };

  const styles = getStyles(darkMode);

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={styles.loadingText}>Loading your learning home...</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <MaterialCommunityIcons name="alert-circle-outline" size={64} color="#ef4444" />
      <Text style={styles.errorText}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={loadInitialData}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      // If no back history, navigate to a default screen
      navigation.navigate('Dashboard');
    }
  };

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <View style={styles.headerLeft}>
            {/* Back Button */}
            <TouchableOpacity 
              style={styles.backButton}
              onPress={handleBackPress}
            >
              <MaterialCommunityIcons 
                name="arrow-left" 
                size={28} 
                color="#ffffff" 
              />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.menuButton}
              onPress={() => navigation.toggleDrawer?.() || navigation.openDrawer?.()}
            >
              <MaterialCommunityIcons name="menu" size={28} color="#ffffff" />
            </TouchableOpacity>
            <View style={styles.greetingContainer}>
              <Text style={styles.greeting}>{greeting},</Text>
              <Text style={styles.userName}>{getUserName()}</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <TouchableOpacity 
              style={styles.iconButton}
              onPress={toggleDarkMode}
            >
              <MaterialCommunityIcons 
                name={darkMode ? "weather-sunny" : "weather-night"} 
                size={24} 
                color="#ffffff" 
              />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.iconButton}
              onPress={() => navigation.navigate('Notifications')}
            >
              <MaterialCommunityIcons name="bell" size={24} color="#ffffff" />
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>3</Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.headerBottom}>
          <View style={styles.statsOverview}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="fire" size={20} color="#ffffff" />
              <Text style={styles.statNumber}>{stats.streakDays || 14}</Text>
              <Text style={styles.statLabel}>Day Streak</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="clock" size={20} color="#ffffff" />
              <Text style={styles.statNumber}>{stats.totalHours || 156}</Text>
              <Text style={styles.statLabel}>Total Hours</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="trophy" size={20} color="#ffffff" />
              <Text style={styles.statNumber}>{stats.certificates || 5}</Text>
              <Text style={styles.statLabel}>Certificates</Text>
            </View>
          </View>
        </View>

        <View style={styles.headerTabs}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <TouchableOpacity 
              style={[styles.tab, activeTab === 'overview' && styles.activeTab]}
              onPress={() => setActiveTab('overview')}
            >
              <MaterialCommunityIcons 
                name="view-dashboard" 
                size={20} 
                color={activeTab === 'overview' ? '#667eea' : '#ffffff'} 
              />
              <Text style={[styles.tabText, activeTab === 'overview' && styles.activeTabText]}>
                Overview
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.tab, activeTab === 'tasks' && styles.activeTab]}
              onPress={() => setActiveTab('tasks')}
            >
              <MaterialCommunityIcons 
                name="clipboard-check" 
                size={20} 
                color={activeTab === 'tasks' ? '#667eea' : '#ffffff'} 
              />
              <Text style={[styles.tabText, activeTab === 'tasks' && styles.activeTabText]}>
                Tasks
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.tab, activeTab === 'progress' && styles.activeTab]}
              onPress={() => setActiveTab('progress')}
            >
              <MaterialCommunityIcons 
                name="chart-line" 
                size={20} 
                color={activeTab === 'progress' ? '#667eea' : '#ffffff'} 
              />
              <Text style={[styles.tabText, activeTab === 'progress' && styles.activeTabText]}>
                Progress
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.tab, activeTab === 'goals' && styles.activeTab]}
              onPress={() => setActiveTab('goals')}
            >
              <MaterialCommunityIcons 
                name="flag" 
                size={20} 
                color={activeTab === 'goals' ? '#667eea' : '#ffffff'} 
              />
              <Text style={[styles.tabText, activeTab === 'goals' && styles.activeTabText]}>
                Goals
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.tab, activeTab === 'resources' && styles.activeTab]}
              onPress={() => setActiveTab('resources')}
            >
              <MaterialCommunityIcons 
                name="book-open" 
                size={20} 
                color={activeTab === 'resources' ? '#667eea' : '#ffffff'} 
              />
              <Text style={[styles.tabText, activeTab === 'resources' && styles.activeTabText]}>
                Resources
              </Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderTodayTasks = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeIn,
          transform: [{ translateY: slideUp }],
        },
      ]}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleContainer}>
          <MaterialCommunityIcons name="clipboard-check" size={24} color="#667eea" />
          <Text style={styles.sectionTitle}>Today's Tasks</Text>
        </View>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => setShowTaskModal(true)}
        >
          <MaterialCommunityIcons name="plus" size={20} color="#ffffff" />
        </TouchableOpacity>
      </View>
      
      <View style={styles.tasksList}>
        {todayTasks.map((task, index) => (
          <Animated.View 
            key={task.id}
            style={[
              styles.taskCard,
              {
                opacity: fadeIn,
                transform: [{ translateY: slideUp }],
              },
            ]}
          >
            <TouchableOpacity 
              style={styles.taskContent}
              onPress={() => toggleTaskCompletion(task.id)}
            >
              <View style={styles.taskLeft}>
                <View style={[styles.priorityDot, { backgroundColor: getPriorityColor(task.priority) }]} />
                <View style={styles.taskInfo}>
                  <Text style={[styles.taskTitle, task.completed && styles.completedTask]}>
                    {task.title}
                  </Text>
                  <View style={styles.taskMeta}>
                    <MaterialCommunityIcons name={getTypeIcon(task.type)} size={14} color="#6b7280" />
                    <Text style={styles.taskMetaText}>{task.course}</Text>
                    <MaterialCommunityIcons name="clock-outline" size={14} color="#6b7280" />
                    <Text style={styles.taskMetaText}>{task.time} • {task.duration}</Text>
                  </View>
                </View>
              </View>
              <TouchableOpacity 
                style={[styles.checkbox, task.completed && styles.checkedBox]}
                onPress={() => toggleTaskCompletion(task.id)}
              >
                {task.completed && (
                  <MaterialCommunityIcons name="check" size={16} color="#ffffff" />
                )}
              </TouchableOpacity>
            </TouchableOpacity>
          </Animated.View>
        ))}
      </View>

      <TouchableOpacity 
        style={styles.viewAllButton}
        onPress={() => navigation.navigate('TaskManager')}
      >
        <Text style={styles.viewAllText}>View All Tasks</Text>
        <MaterialCommunityIcons name="chevron-right" size={20} color="#667eea" />
      </TouchableOpacity>
    </Animated.View>
  );

  const renderWeeklyProgress = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeIn,
          transform: [{ translateY: slideUp }],
        },
      ]}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleContainer}>
          <MaterialCommunityIcons name="chart-line" size={24} color="#667eea" />
          <Text style={styles.sectionTitle}>Weekly Progress</Text>
        </View>
        <TouchableOpacity onPress={() => navigation.navigate('ProgressDetails')}>
          <Text style={styles.seeDetailsText}>Details</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.progressChartContainer}>
        <View style={styles.progressBars}>
          {weeklyProgress.map((day, index) => (
            <View key={day.day} style={styles.progressBarContainer}>
              <Text style={styles.progressDay}>{day.day}</Text>
              <View style={styles.progressBarBackground}>
                <LinearGradient
                  colors={day.completed ? gradients.success : gradients.warning}
                  style={[styles.progressBarFill, { height: `${(day.hours / 8) * 100}%` }]}
                />
              </View>
              <Text style={styles.progressHours}>{day.hours}h</Text>
              <View style={[styles.completionDot, { backgroundColor: day.completed ? '#10b981' : '#6b7280' }]} />
            </View>
          ))}
        </View>
      </View>

      <View style={styles.progressStats}>
        <View style={styles.progressStat}>
          <Text style={styles.progressStatNumber}>
            {weeklyProgress.reduce((sum, day) => sum + day.hours, 0).toFixed(1)}
          </Text>
          <Text style={styles.progressStatLabel}>Hours This Week</Text>
        </View>
        <View style={styles.progressStat}>
          <Text style={styles.progressStatNumber}>
            {weeklyProgress.filter(day => day.completed).length}/7
          </Text>
          <Text style={styles.progressStatLabel}>Days Completed</Text>
        </View>
        <View style={styles.progressStat}>
          <Text style={styles.progressStatNumber}>
            {(weeklyProgress.reduce((sum, day) => sum + day.hours, 0) / 7).toFixed(1)}
          </Text>
          <Text style={styles.progressStatLabel}>Avg Daily Hours</Text>
        </View>
      </View>
    </Animated.View>
  );

  const renderUpcomingSessions = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeIn,
          transform: [{ translateY: slideUp }],
        },
      ]}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleContainer}>
          <MaterialCommunityIcons name="calendar-clock" size={24} color="#667eea" />
          <Text style={styles.sectionTitle}>Upcoming Sessions</Text>
        </View>
        <TouchableOpacity onPress={() => navigation.navigate('CalendarView')}>
          <Text style={styles.seeAllText}>View Calendar</Text>
        </TouchableOpacity>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.sessionsScroll}>
        {upcomingSessions.map((session, index) => (
          <TouchableOpacity 
            key={session.id}
            style={styles.sessionCard}
            onPress={() => Linking.openURL(session.joinLink).catch(console.error)}
          >
            <LinearGradient
              colors={gradients.blue}
              style={styles.sessionHeader}
            >
              <View style={styles.sessionTypeBadge}>
                <Text style={styles.sessionTypeText}>{String(session.type ?? '').toUpperCase()}</Text>
              </View>
              <MaterialCommunityIcons name="video" size={32} color="#ffffff" />
            </LinearGradient>
            
            <View style={styles.sessionContent}>
              <Text style={styles.sessionTitle} numberOfLines={2}>{session.title}</Text>
              <View style={styles.sessionInstructor}>
                <MaterialCommunityIcons name="account" size={14} color="#6b7280" />
                <Text style={styles.sessionInstructorText}>{session.instructor}</Text>
              </View>
              <View style={styles.sessionDetails}>
                <View style={styles.sessionDetail}>
                  <MaterialCommunityIcons name="clock-outline" size={14} color="#6b7280" />
                  <Text style={styles.sessionDetailText}>{session.time}</Text>
                </View>
                <View style={styles.sessionDetail}>
                  <MaterialCommunityIcons name="timer" size={14} color="#6b7280" />
                  <Text style={styles.sessionDetailText}>{session.duration}</Text>
                </View>
                <View style={styles.sessionDetail}>
                  <MaterialCommunityIcons name="monitor" size={14} color="#6b7280" />
                  <Text style={styles.sessionDetailText}>{session.platform}</Text>
                </View>
              </View>
              
              <TouchableOpacity 
                style={styles.joinButton}
                onPress={() => Linking.openURL(session.joinLink).catch(console.error)}
              >
                <MaterialCommunityIcons name="video-plus" size={16} color="#ffffff" />
                <Text style={styles.joinButtonText}>Join Session</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </Animated.View>
  );

  const renderLearningGoals = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeIn,
          transform: [{ translateY: slideUp }],
        },
      ]}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleContainer}>
          <MaterialCommunityIcons name="flag" size={24} color="#667eea" />
          <Text style={styles.sectionTitle}>Learning Goals</Text>
        </View>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => setShowGoalModal(true)}
        >
          <MaterialCommunityIcons name="plus" size={20} color="#ffffff" />
        </TouchableOpacity>
      </View>

      <View style={styles.goalsGrid}>
        {learningGoals.map((goal) => (
          <TouchableOpacity 
            key={goal.id}
            style={styles.goalCard}
            onPress={() => navigation.navigate('GoalDetails', { goal })}
          >
            <LinearGradient
              colors={gradients.purple}
              style={styles.goalHeader}
            >
              <MaterialCommunityIcons 
                name={goal.type === 'course' ? 'book-open' : 
                      goal.type === 'exam' ? 'clipboard-text' : 
                      goal.type === 'project' ? 'hammer-wrench' : 'chart-line'} 
                size={28} 
                color="#ffffff" 
              />
              <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(goal.priority) }]}>
                <Text style={styles.priorityBadgeText}>{goal.priority}</Text>
              </View>
            </LinearGradient>
            
            <View style={styles.goalContent}>
              <Text style={styles.goalTitle} numberOfLines={2}>{goal.title}</Text>
              <Text style={styles.goalTarget} numberOfLines={2}>{goal.target}</Text>
              
              <View style={styles.progressContainer}>
                <View style={styles.progressBar}>
                  <LinearGradient
                    colors={gradients.success}
                    style={[styles.progressFill, { width: `${goal.progress}%` }]}
                  />
                </View>
                <Text style={styles.progressText}>{goal.progress}%</Text>
              </View>
              
              <View style={styles.goalFooter}>
                <View style={styles.deadline}>
                  <MaterialCommunityIcons name="calendar" size={14} color="#6b7280" />
                  <Text style={styles.deadlineText}>{goal.deadline}</Text>
                </View>
                <TouchableOpacity style={styles.editButton}>
                  <MaterialCommunityIcons name="pencil" size={16} color="#667eea" />
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <TouchableOpacity 
        style={styles.viewAllButton}
        onPress={() => navigation.navigate('GoalsManager')}
      >
        <Text style={styles.viewAllText}>View All Goals</Text>
        <MaterialCommunityIcons name="chevron-right" size={20} color="#667eea" />
      </TouchableOpacity>
    </Animated.View>
  );

  const renderQuickAccess = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeIn,
          transform: [{ translateY: slideUp }],
        },
      ]}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleContainer}>
          <MaterialCommunityIcons name="rocket-launch" size={24} color="#667eea" />
          <Text style={styles.sectionTitle}>Quick Access</Text>
        </View>
        <TouchableOpacity onPress={() => navigation.navigate('AllResources')}>
          <Text style={styles.seeAllText}>All Resources</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.quickAccessGrid}>
        {quickAccess.map((item) => (
          <TouchableOpacity 
            key={item.id}
            style={styles.quickAccessCard}
            onPress={() => navigation.navigate(item.screen)}
          >
            <LinearGradient
              colors={[item.color, `${item.color}80`]}
              style={styles.quickAccessIcon}
            >
              <MaterialCommunityIcons name={item.icon} size={28} color="#ffffff" />
            </LinearGradient>
            <Text style={styles.quickAccessTitle}>{item.title}</Text>
            <Text style={styles.quickAccessSubtitle}>{item.subtitle}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </Animated.View>
  );

  const renderRecentActivity = () => (
    <Animated.View 
      style={[
        styles.section,
        {
          opacity: fadeIn,
          transform: [{ translateY: slideUp }],
        },
      ]}
    >
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleContainer}>
          <MaterialCommunityIcons name="history" size={24} color="#667eea" />
          <Text style={styles.sectionTitle}>Recent Activity</Text>
        </View>
        <TouchableOpacity onPress={() => navigation.navigate('ActivityLog')}>
          <Text style={styles.seeAllText}>View All</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.activityList}>
        {recentActivity.map((activity, index) => (
          <View key={activity.id} style={styles.activityItem}>
            <View style={styles.activityIconContainer}>
              <LinearGradient
                colors={gradients.blue}
                style={styles.activityIcon}
              >
                <MaterialCommunityIcons name={getTypeIcon(activity.type)} size={20} color="#ffffff" />
              </LinearGradient>
            </View>
            <View style={styles.activityContent}>
              <Text style={styles.activityTitle}>{activity.title}</Text>
              <View style={styles.activityMeta}>
                <Text style={styles.activityCourse}>{activity.course}</Text>
                <Text style={styles.activityTime}>{activity.time}</Text>
              </View>
            </View>
            {activity.score && (
              <View style={styles.activityScore}>
                <Text style={styles.activityScoreText}>{activity.score}%</Text>
              </View>
            )}
          </View>
        ))}
      </View>
    </Animated.View>
  );

  const renderTaskModal = () => (
    <Modal
      visible={showTaskModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowTaskModal(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowTaskModal(false)}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback>
            <Animated.View 
              style={[
                styles.modalContent,
                {
                  transform: [{ translateY: slideUp }],
                  opacity: fadeIn,
                },
              ]}
            >
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Add New Task</Text>
                <TouchableOpacity onPress={() => setShowTaskModal(false)}>
                  <MaterialCommunityIcons name="close" size={24} color="#6b7280" />
                </TouchableOpacity>
              </View>
              
              <TextInput
                style={styles.modalInput}
                placeholder="Enter task description..."
                value={newTask}
                onChangeText={setNewTask}
                placeholderTextColor="#9ca3af"
                multiline
              />
              
              <View style={styles.modalActions}>
                <TouchableOpacity 
                  style={[styles.modalButton, styles.cancelButton]}
                  onPress={() => setShowTaskModal(false)}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.modalButton, styles.saveButton]}
                  onPress={addNewTask}
                >
                  <Text style={styles.saveButtonText}>Add Task</Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  const renderGoalModal = () => (
    <Modal
      visible={showGoalModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowGoalModal(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowGoalModal(false)}>
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback>
            <Animated.View 
              style={[
                styles.modalContent,
                {
                  transform: [{ translateY: slideUp }],
                  opacity: fadeIn,
                },
              ]}
            >
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Add New Goal</Text>
                <TouchableOpacity onPress={() => setShowGoalModal(false)}>
                  <MaterialCommunityIcons name="close" size={24} color="#6b7280" />
                </TouchableOpacity>
              </View>
              
              <TextInput
                style={styles.modalInput}
                placeholder="Goal title..."
                value={newGoal.title}
                onChangeText={(text) => setNewGoal(prev => ({ ...prev, title: text }))}
                placeholderTextColor="#9ca3af"
              />
              
              <TextInput
                style={[styles.modalInput, styles.textArea]}
                placeholder="Target description..."
                value={newGoal.target}
                onChangeText={(text) => setNewGoal(prev => ({ ...prev, target: text }))}
                placeholderTextColor="#9ca3af"
                multiline
              />
              
              <TextInput
                style={styles.modalInput}
                placeholder="Deadline (YYYY-MM-DD)"
                value={newGoal.deadline}
                onChangeText={(text) => setNewGoal(prev => ({ ...prev, deadline: text }))}
                placeholderTextColor="#9ca3af"
              />
              
              <View style={styles.modalActions}>
                <TouchableOpacity 
                  style={[styles.modalButton, styles.cancelButton]}
                  onPress={() => setShowGoalModal(false)}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={[styles.modalButton, styles.saveButton]}
                  onPress={addNewGoal}
                >
                  <Text style={styles.saveButtonText}>Add Goal</Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'tasks':
        return (
          <>
            {renderTodayTasks()}
            {renderUpcomingSessions()}
            {renderRecentActivity()}
          </>
        );
      case 'progress':
        return (
          <>
            {renderWeeklyProgress()}
            {renderLearningGoals()}
          </>
        );
      case 'goals':
        return (
          <>
            {renderLearningGoals()}
            {renderTodayTasks()}
          </>
        );
      case 'resources':
        return (
          <>
            {renderQuickAccess()}
            {renderRecentActivity()}
          </>
        );
      default: // overview
        return (
          <>
            {renderTodayTasks()}
            {renderWeeklyProgress()}
            {renderUpcomingSessions()}
            {renderLearningGoals()}
            {renderQuickAccess()}
            {renderRecentActivity()}
          </>
        );
    }
  };

  if (loading) {
    return renderLoading();
  }

  if (error && !isRefreshing) {
    return renderError();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
      >
        {renderContent()}
        
        {/* Add some bottom padding for better scrolling */}
        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Floating Action Button for Quick Actions */}
      <TouchableOpacity 
        style={styles.fab}
        onPress={() => {
          Alert.alert(
            'Quick Actions',
            'Jump to a section',
            [
              { text: 'Video Library', onPress: () => navigation.navigate('VideoLibrary') },
              { text: 'Practice Exams', onPress: () => navigation.navigate('PracticeExams') },
              { text: 'E-Books', onPress: () => navigation.navigate('EngineeringBooksScreen') },
              { text: 'Research Papers', onPress: () => navigation.navigate('ResearchPapers') },
              { text: 'Cancel', style: 'cancel' },
            ],
            { cancelable: true }
          );
        }}
      >
        <LinearGradient
          colors={gradients.primary}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="lightning-bolt" size={24} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>

      {/* Modals */}
      {renderTaskModal()}
      {renderGoalModal()}
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 100,
  },
  bottomPadding: {
    height: 100,
  },
  // Header Styles
  header: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  headerGradient: {
    paddingTop: Platform.OS === 'ios' ? 50 : 30,
    paddingBottom: 20,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    marginRight: 8,
  },
  menuButton: {
    marginRight: 16,
  },
  greetingContainer: {
    flex: 1,
  },
  greeting: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 2,
  },
  userName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconButton: {
    marginLeft: 16,
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -5,
    right: -5,
    backgroundColor: '#ef4444',
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  headerBottom: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  statsOverview: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 16,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statDivider: {
    width: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    marginHorizontal: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    marginVertical: 4,
  },
  statLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  headerTabs: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    marginBottom: 4,
  },
  activeTab: {
    backgroundColor: '#ffffff',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  activeTabText: {
    color: '#667eea',
  },
  // Section Styles
  section: {
    marginTop: 20,
    paddingHorizontal: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginLeft: 8,
  },
  seeAllText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
  },
  seeDetailsText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#667eea',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  // Task Styles
  tasksList: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  taskCard: {
    marginBottom: 12,
  },
  taskContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: darkMode ? '#4a5568' : '#f9fafb',
    borderRadius: 12,
  },
  taskLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  priorityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 12,
  },
  taskInfo: {
    flex: 1,
  },
  taskTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  completedTask: {
    textDecorationLine: 'line-through',
    color: darkMode ? '#a0aec0' : '#9ca3af',
  },
  taskMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  taskMetaText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
    marginRight: 12,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: darkMode ? '#a0aec0' : '#d1d5db',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkedBox: {
    backgroundColor: '#10b981',
    borderColor: '#10b981',
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    padding: 12,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 12,
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
    marginRight: 4,
  },
  // Progress Chart Styles
  progressChartContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  progressBars: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 200,
  },
  progressBarContainer: {
    alignItems: 'center',
    flex: 1,
  },
  progressDay: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 8,
  },
  progressBarBackground: {
    width: 20,
    height: 120,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 10,
    overflow: 'hidden',
    justifyContent: 'flex-end',
  },
  progressBarFill: {
    width: '100%',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  progressHours: {
    fontSize: 12,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 4,
  },
  completionDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 4,
  },
  progressStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 16,
    marginTop: 16,
  },
  progressStat: {
    alignItems: 'center',
    flex: 1,
  },
  progressStatNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  progressStatLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  // Session Card Styles
  sessionsScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  sessionCard: {
    width: 280,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginRight: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sessionHeader: {
    padding: 20,
    alignItems: 'center',
    position: 'relative',
  },
  sessionTypeBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  sessionTypeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  sessionContent: {
    padding: 16,
  },
  sessionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 8,
  },
  sessionInstructor: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sessionInstructorText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  sessionDetails: {
    marginBottom: 16,
  },
  sessionDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  sessionDetailText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 6,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 10,
    borderRadius: 8,
  },
  joinButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  // Goals Styles
  goalsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  goalCard: {
    width: '48%',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginHorizontal: '1%',
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  goalHeader: {
    padding: 16,
    alignItems: 'center',
    position: 'relative',
  },
  priorityBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  priorityBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  goalContent: {
    padding: 12,
  },
  goalTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  goalTarget: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginBottom: 12,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 3,
    marginRight: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  goalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  deadline: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deadlineText: {
    fontSize: 10,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  editButton: {
    padding: 4,
  },
  // Quick Access Styles
  quickAccessGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  quickAccessCard: {
    width: '30%',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 12,
    marginHorizontal: '1.66%',
    marginBottom: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  quickAccessIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  quickAccessTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 2,
    textAlign: 'center',
  },
  quickAccessSubtitle: {
    fontSize: 10,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  // Activity Styles
  activityList: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  activityItem: {
    borderBottomWidth: 0,
  },
  activityIconContainer: {
    marginRight: 12,
  },
  activityIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  activityMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  activityCourse: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginRight: 8,
  },
  activityTime: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  activityScore: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  activityScoreText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    minHeight: 300,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  modalInput: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    marginRight: 8,
  },
  saveButton: {
    backgroundColor: '#667eea',
    marginLeft: 8,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  // FAB Styles
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Loading and Error Styles
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#374151',
    textAlign: 'center',
    marginVertical: 16,
  },
  retryButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default LearningHomeScreen;